import { Flex, Slider, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  translateIdentity,
  type StaticTextTranslator,
} from "../../utils";

import styles from "./useScrubbableAudio.module.css";

interface UseScrubbableAudioProps {
  disabled: boolean;
  /** When omitted, scrubbing/duration UI is suppressed (audio still plays). */
  duration?: number;
  srcUrl: string;
  translate?: StaticTextTranslator;
}

const showError = (message: string) =>
  notifications.show({ message, color: "red" });

export const useScrubbableAudio = ({
  disabled,
  duration,
  srcUrl,
  translate = translateIdentity,
}: UseScrubbableAudioProps) => {
  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null);
  const [sliderValue, setSliderValue] = useState(0);
  const [scrubbing, setScrubbing] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    setPlaying(false);
    setScrubbing(false);
    setSliderValue(0);
    if (audioRef) {
      audioRef.currentTime = 0;
    }
  }, [srcUrl, audioRef]);

  const setSliderAndPlaybackTime = useCallback(
    (value: number) => {
      if (audioRef) {
        setSliderValue(() => {
          audioRef.currentTime = value;
          return value;
        });
      }
    },
    [audioRef],
  );

  const playWithCatch = useCallback(async () => {
    try {
      await audioRef?.play();
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "NotAllowedError") {
          showError(
            translate("Unable to play recording due to browser restrictions"),
          );
          return;
        }
        if (error.name === "NotSupportedError") {
          showError(
            translate(
              "Playback of this type of media is not supported by your browser",
            ),
          );
          return;
        }
      }
      showError(translate("There was a problem playing your recording"));
    }
  }, [audioRef, translate]);

  const audioElement = useMemo(
    () => (
      <audio
        onEnded={() => setPlaying(false)}
        onPause={() => {
          setPlaying(false);
          if (!scrubbing) {
            setSliderAndPlaybackTime(0);
          }
        }}
        onPlay={() => setPlaying(true)}
        onTimeUpdate={() => {
          if (audioRef && !scrubbing) {
            setSliderValue(audioRef.currentTime);
          }
        }}
        ref={setAudioRef}
        src={srcUrl}
      />
    ),
    [audioRef, scrubbing, setSliderAndPlaybackTime, srcUrl],
  );

  const scrubbingElement = useMemo(() => {
    if (typeof duration === "number" && Number.isFinite(duration)) {
      return (
        <Flex
          align="center"
          className={styles.progressBarWrapper}
          justify="center"
        >
          <Slider
            classNames={{
              root: styles.progressBarWrapper,
              thumb: styles.trackThumb,
              track: styles.track,
            }}
            disabled={disabled}
            label={null}
            max={duration}
            min={0}
            onChange={(value) => {
              setScrubbing(true);
              setSliderValue(value);
            }}
            onChangeEnd={(value) => {
              setScrubbing(false);
              setSliderAndPlaybackTime(value);
              if (playing) {
                playWithCatch();
              }
            }}
            showLabelOnHover={false}
            step={0.1}
            value={sliderValue}
          />
        </Flex>
      );
    }
    return null;
  }, [
    disabled,
    duration,
    playWithCatch,
    playing,
    setSliderAndPlaybackTime,
    sliderValue,
  ]);

  const durationElement = useMemo(() => {
    if (
      typeof audioRef?.currentTime === "number" &&
      typeof duration === "number" &&
      Number.isFinite(duration)
    ) {
      return (
        <Text className={styles.duration}>
          {Math.max(Math.round(duration - audioRef.currentTime), 0)}s
        </Text>
      );
    }
    return null;
  }, [audioRef?.currentTime, duration]);

  return {
    audioElement,
    audioRef,
    durationElement,
    playWithCatch,
    playing,
    scrubbingElement,
    setPlaybackTime: setSliderAndPlaybackTime,
  };
};
